import * as intlTelInput from 'intl-tel-input';
import {DOM} from "../../../../../public/js/utils/DOM";
import {Networking} from "../../../../../public/js/utils/Netwtorking";
import {RequestTypes} from "../../../../../public/js/definitions/utils/networking/RequestTypes";
import {SentryReportCustomError} from "../../../../../public/js/utils/Sentry/Sentry";
import {ElementValidatorUtil} from "../../../../../public/js/utils/Validator/ElementValidatorUtil";
import {Iti, SomeOptions} from "intl-tel-input";

/**
 * Implementation of PhoneInput form element
 * Use intl library for phone number validation
 * Creates data-data prop with number data or null
 */
export namespace PhoneInput {

    class Config {
        public static readonly countryDataName = 'country';
        public static readonly utilsScriptLocation: string = "/yarn/intl-tel-input/build/js/utils.js";
        public static readonly errorMessageDataName = "error";
        public static readonly autofillClassName = 'PhoneInput-input-autoFill';
        public static readonly phoneInputElementClassName = 'PhoneInput-input';
        public static readonly controlClassName = 'PhoneInput';
        public static readonly autofillOptionClassName = "PhoneInput-input-autoFill-container-item";
        public static readonly autofillOptionNumberDataName = "number";
        public static readonly autofillOptionNumberInternationalDataName = "international";
        public static readonly autofillOptionCountryDataName = "country";
        public static readonly ipInfoApiEndPoint = "https://ipinfo.io";
    }

    export class Control {

        /**
         * Initialize all elements in form
         * @param container
         */
        public static initialize(container: HTMLFormElement) : void {

            const elements = DOM.Parser.getHtmlElements(Config.controlClassName, container, true);
            for (let i = 0; i < elements.length; i++) {
                const element = elements[i] as HTMLDivElement;
                this.initializeElement(element);
            }
        }

        /**
         * Initialize form concrete element
         * @param element - phone input element
         * @private
         */
        private static initializeElement(element: HTMLDivElement) : void {

            if (element.parentElement === null) {
                SentryReportCustomError('Error loading phone input - parent element missing!');
                return;
            }

            DOM.Parser.getHtmlElementByClass('customDesign-forms-labelContainer', element.parentElement).classList.add('active');
            const inputElement = DOM.Parser.getHtmlElementByQuery('input', element) as HTMLInputElement;

            // @ts-ignore
            // noinspection JSUnusedGlobalSymbols
            const intl = intlTelInput(inputElement, {
                allowDropdown: true,
                formatOnDisplay: false,
                countryOrder: ["cz", "sk", "us", "gb"],
                initialCountry: DOM.Parser.getDataByNameFromElement(Config.countryDataName, inputElement),
                loadUtilsOnInit: location.origin + Config.utilsScriptLocation,
                geoIpLookup(success) {
                    void Networking.NetworkingController.createRequest({
                        type: RequestTypes.GET, url: Config.ipInfoApiEndPoint, successCallback: (result: any) => {
                            success(result.country);
                        }, dontProcessPayload: true,
                    });
                },

            } as SomeOptions) as Iti;

            // Todo fix initial wrong input state
            setTimeout(() => { inputElement.dispatchEvent(new Event('input')); }, 100);
            setTimeout(() => { inputElement.dispatchEvent(new Event('input')); }, 200);
            setTimeout(() => { inputElement.dispatchEvent(new Event('input')); }, 400);
            setTimeout(() => { inputElement.dispatchEvent(new Event('input')); }, 800);

            // Initialize auto-fill and country re-validate
            this.triggerAutoFill(element, intl);
        }

        /**
         * Autofill functionality triggers
         * @param element - input element
         * @param intl - intl instance
         * @private
         */
        private static triggerAutoFill(element: HTMLDivElement, intl: any) : void {

            // Check
            const autoFill = element.querySelector('.' + Config.autofillClassName) as HTMLDivElement|null;
            if (autoFill === null) { return; }
            const input = DOM.Parser.getHtmlElementByQuery('input', element) as HTMLInputElement;

            // Callbacks
            input.addEventListener('focus', () => { autoFill.style.display = 'block'; })
            input.addEventListener('input', () => { this.processAutoFillUpdate(input, autoFill); });
            input.addEventListener('change', () => { this.processAutoFillUpdate(input, autoFill); });
            input.addEventListener('update', () => { this.processAutoFillUpdate(input, autoFill); });
            input.addEventListener('keyup', () => { this.processAutoFillUpdate(input, autoFill); });

            // Outside click trigger - to hide
            window.addEventListener('click', (evt: MouseEvent) => {
                if (autoFill.style.display === 'none') { return; }
                const clickedInside = DOM.Parser.parentContainsTheClass(Config.phoneInputElementClassName, evt.target as HTMLElement);
                if (clickedInside) { return; }
                autoFill.style.display = 'none';
            });

            // Option clicked trigger
            const optionTriggers = DOM.Parser.getHtmlElements(Config.autofillOptionClassName, autoFill, true);
            for (let i = 0; i < optionTriggers.length; i++) {
                const optionTrigger = optionTriggers[i];
                const optionNumber = DOM.Parser.getDataByNameFromElement(Config.autofillOptionNumberDataName, optionTrigger);
                const optionCountry = DOM.Parser.getDataByNameFromElement(Config.autofillOptionCountryDataName, optionTrigger);
                optionTrigger.addEventListener('click', (event: MouseEvent) => {
                    event.stopPropagation(); event.preventDefault();
                    intl.setCountry(optionCountry);
                    intl.setNumber(optionNumber);
                    autoFill.style.display = 'none';
                    input.dispatchEvent(new Event('input'));
                });
            }
        }

        /**
         * Update autofill block
         * @param input - input element
         * @param autoFill - autofill element
         * @private
         */
        private static processAutoFillUpdate(input: HTMLInputElement, autoFill: HTMLDivElement) : void {

            // Set required variables
            const inputValue = input.value.toString().replace(/\D/g, '').trim();
            const inputValueNumbers = input.value.toString().replace(/\D/g, '').split('');
            const emptyInputValue = inputValue.length === 0;
            const options = DOM.Parser.getHtmlElements(Config.autofillOptionClassName, autoFill, true);

            // Create regex to show bold
            let regex: string|null = null;
            if (inputValueNumbers.length !== 0) {
                regex = '.*(';
                for (const inputNumber of inputValueNumbers) {
                    regex += inputNumber + '[\\(\\)\\s]*';
                }
                regex += ')';
            }

            // Process all options
            let shown = 0;
            for (let i = 0; i < options.length; i++) {

                // Set required parameters
                const option = options[i];
                const optionNumber = DOM.Parser.getDataByNameFromElement(Config.autofillOptionNumberInternationalDataName, option).replace(/\D/g, '');
                const optionText = option.textContent?.toString().trim() as string;
                const optionMatch = optionNumber.includes(inputValue);
                const regexCompiled = regex ? new RegExp(regex, 'gs') : null;

                // Protect max shown
                if (shown >= 5) {
                    option.style.display = 'none';
                    continue;
                }

                // If empty input value, show all options (use max limit)
                if (emptyInputValue) {
                    option.style.display = 'block';
                    shown++;
                }

                // If found - show option
                else if (optionMatch) {

                    option.style.display = 'block';
                    shown++;

                    // Bold text
                    if (regexCompiled) {
                        const find = regexCompiled.exec(optionText);
                        if (find && find[1]) { option.innerHTML = optionText.replace(find[1], '<b>' + find[1] + '</b>'); }
                    }
                }

                // If not found hide option
                else {
                    option.style.display = 'none';
                }
            }
        }
    }

    export class Helpers {

        /**
         * Validate phone number input
         * Extends validator (called from there)
         * Checks valid number using the intl lib + check if allowed character
         * @param input - phone input element
         * @private
         */
        public static validatePhoneInput(input: HTMLInputElement|HTMLTextAreaElement) : void {

            // @ts-ignore
            const iti = intlTelInput.getInstance(input as HTMLInputElement) as Iti|null;
            if (!iti) { return; }
            const problemString = DOM.Parser.getDataByNameFromElement(Config.errorMessageDataName, input);

            if (input.value.trim().length > 0 && (!iti.isValidNumber() || /[^0-9+\-()\s]/.test(input.value.trim()))) {
                input.dataset.valid = "false";
                ElementValidatorUtil.updateHelpElement(input, problemString, false);
                return;
            } else {

                // Check if dial code present, if empty value -> probably in initialization mode, don't report
                const dialCode = iti.getSelectedCountryData().dialCode;
                if (!dialCode) {
                    if (input.value.trim().length > 0) {
                        SentryReportCustomError('PhoneInput: Impossible to find dial code', {inputValue: input.value.trim()});
                    }
                    return;
                }

                const numberNational = iti.getNumber(2).replace(/\D/g, '').trim();
                const numberInternational = iti.getNumber(1).replace(/[^0-9+]/g, '').trim();
                input.dataset.data = JSON.stringify({
                    dialCode: parseInt(dialCode),
                    countryCode: iti.getSelectedCountryData().iso2,
                    numberNational: numberNational.length > 0 ? numberNational : null,
                    numberInternational: numberInternational.length > 0 ? numberInternational : null,
                });
            }

            ElementValidatorUtil.updateHelpElement(input, problemString, true);
        }
    }
}